import { db } from '@/services/storage/db';

/** Countdown milestones (days before opener) that trigger a season notification. */
const COUNTDOWN_MILESTONES = [14, 7, 1] as const;

/** localStorage key for tracking which notifications have already fired. */
const NOTIFIED_STORAGE_KEY = 'ridgen:notified';

// ── Date helpers ─────────────────────────────────────────────────────────────

function getTodayDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Whole-day difference: positive = toDateStr is in the future relative to fromDateStr.
 * Both strings must be YYYY-MM-DD.
 */
export function daysBetween(fromDateStr: string, toDateStr: string): number {
  const from = new Date(fromDateStr + 'T00:00:00');
  const to = new Date(toDateStr + 'T00:00:00');
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

// ── Notification dedup tracking ───────────────────────────────────────────────

export function getNotifiedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(NOTIFIED_STORAGE_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

export function markNotified(key: string): void {
  const set = getNotifiedSet();
  set.add(key);
  localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify([...set]));
}

export function hasBeenNotified(key: string): boolean {
  return getNotifiedSet().has(key);
}

/** Clear the notified set — useful in tests and for manual reset. */
export function clearNotifiedSet(): void {
  localStorage.removeItem(NOTIFIED_STORAGE_KEY);
}

// ── Notification display ──────────────────────────────────────────────────────

export async function showNotification(
  title: string,
  body: string,
  tag: string,
  url = '/',
): Promise<void> {
  if (!navigator.serviceWorker) return;
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(title, {
    body,
    tag,
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    data: { url },
  });
}

// ── Notification logic ────────────────────────────────────────────────────────

/**
 * Fire "due soon" notifications for incomplete tasks within the lead window.
 * Returns the number of new notifications fired.
 */
export async function notifyUpcomingTasks(
  tasks: Array<{ id: string; title: string; dueDate: string; completed: boolean; seasonId: string }>,
  leadDays: number,
  today: string,
): Promise<number> {
  let fired = 0;
  for (const task of tasks) {
    if (task.completed) continue;
    const daysUntil = daysBetween(today, task.dueDate);
    if (daysUntil < 0 || daysUntil > leadDays) continue;
    const key = `task:${task.id}:${task.dueDate}`;
    if (hasBeenNotified(key)) continue;
    const dueLabel =
      daysUntil === 0 ? 'today' : `in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
    await showNotification(
      task.title,
      `Due ${dueLabel}`,
      key,
      `/seasons/${task.seasonId}/tasks/${task.id}`,
    );
    markNotified(key);
    fired++;
  }
  return fired;
}

/**
 * Fire an overdue-tasks summary notification if there are any past-due incomplete tasks.
 */
export async function notifyOverdueTasks(
  tasks: Array<{ id: string; completed: boolean; dueDate: string; seasonId: string }>,
  today: string,
): Promise<void> {
  const overdue = tasks.filter((t) => !t.completed && t.dueDate < today);
  if (overdue.length === 0) return;
  const key = `overdue:${today}`;
  if (hasBeenNotified(key)) return;
  await showNotification(
    `${overdue.length} overdue task${overdue.length === 1 ? '' : 's'}`,
    'Some tasks are past their due date',
    key,
    '/checklist',
  );
  markNotified(key);
}

/**
 * Fire countdown notifications at 14d, 7d, and 1d milestones before each season opener.
 */
export async function notifySeasonCountdowns(
  seasons: Array<{ id: string; name: string; openerDate: string }>,
  today: string,
): Promise<void> {
  for (const season of seasons) {
    const daysUntil = daysBetween(today, season.openerDate);
    for (const milestone of COUNTDOWN_MILESTONES) {
      if (daysUntil !== milestone) continue;
      const key = `season:${season.id}:${milestone}d`;
      if (hasBeenNotified(key)) continue;
      await showNotification(
        `${season.name} opens in ${milestone} day${milestone === 1 ? '' : 's'}`,
        'Make sure your prep tasks are on track',
        key,
        `/seasons/${season.id}`,
      );
      markNotified(key);
    }
  }
}

// ── Main entry point ──────────────────────────────────────────────────────────

/**
 * Check upcoming tasks and season countdowns and fire any pending notifications.
 * Call this on app foreground when notifications are enabled and permission is granted.
 */
export async function checkAndNotify(notificationLeadDays: number): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (!navigator.serviceWorker) return;

  const today = getTodayDateStr();

  try {
    const [tasks, seasons] = await Promise.all([
      db.getUpcomingTasks(200),
      db.getActiveSeasons(),
    ]);

    await notifyUpcomingTasks(tasks, notificationLeadDays, today);
    await notifyOverdueTasks(tasks, today);
    await notifySeasonCountdowns(seasons, today);
  } catch (err) {
    console.error('[NotificationScheduler] Failed to check notifications:', err);
  }
}
